import { Timestamp } from 'firebase/firestore';
import { Lead, LeadStatus, LeadDetermination } from '../types/lead.types';
import { SchoolSegment } from '../types/project.types';
import { CsvExportTemplate, CsvImportTemplate, CsvExportContext, ParsedImportRow, LeadMatchKey } from '../types/csvTemplate.types';

// =========================================================================
// Value maps — HubSpot dropdowns require exact property values.
// =========================================================================

// Internal grade values (QuickLeadCapture uses 'Kindergarten'/'Pre-K'/'1st'…,
// the multi-step form uses 'K'/'1'/'2'…) → HubSpot grade dropdown. The HC
// client is K-8, so Pre-K and 9-12 have no target value and map to ''.
const GRADE_TO_HUBSPOT: Record<string, string> = {
  'k': 'Kinder', 'kinder': 'Kinder', 'kindergarten': 'Kinder',
  '1': '1st', '1st': '1st',
  '2': '2nd', '2nd': '2nd',
  '3': '3rd', '3rd': '3rd',
  '4': '4th', '4th': '4th',
  '5': '5th', '5th': '5th',
  '6': '6th', '6th': '6th',
  '7': '7th', '7th': '7th',
  '8': '8th', '8th': '8th',
};

export const normalizeGradeForHubspot = (grade?: string): string =>
  grade ? (GRADE_TO_HUBSPOT[grade.trim().toLowerCase()] || '') : '';

// Numeric grade for grade-banded campus codes. K/Kinder=0, Pre-K=-1, 1..12.
const GRADE_NUMBER: Record<string, number> = {
  'pre-k': -1, 'prek': -1, 'pk': -1,
  'k': 0, 'kinder': 0, 'kindergarten': 0,
  '1': 1, '1st': 1, '2': 2, '2nd': 2, '3': 3, '3rd': 3, '4': 4, '4th': 4,
  '5': 5, '5th': 5, '6': 6, '6th': 6, '7': 7, '7th': 7, '8': 8, '8th': 8,
  '9': 9, '9th': 9, '10': 10, '10th': 10, '11': 11, '11th': 11, '12': 12, '12th': 12,
};
export const gradeToNumber = (grade?: string): number | null =>
  grade ? (GRADE_NUMBER[grade.trim().toLowerCase()] ?? null) : null;

// Resolve the HubSpot "Campus" code for a student in a segment. Honors a
// grade-banded split (e.g. Metro North → MNE K-5 / MNM 6-8), else the fixed
// hubspotCampus code, else the segment name.
export const resolveCampusCode = (seg: SchoolSegment | undefined, childGrade?: string): string => {
  if (!seg) return '';
  if (seg.hubspotCampusGradeSplit && seg.hubspotCampusGradeSplit.length > 0) {
    const n = gradeToNumber(childGrade);
    if (n != null) {
      const rule = seg.hubspotCampusGradeSplit.find(r => n >= r.minGrade && n <= r.maxGrade);
      if (rule) return rule.code;
    }
  }
  return seg.hubspotCampus || seg.name || '';
};

// Family language → HubSpot "Language/Idioma".
const LANGUAGE_TO_HUBSPOT: Record<string, string> = {
  'en': 'English', 'english': 'English', 'eng': 'English',
  'es': 'Español', 'español': 'Español', 'espanol': 'Español', 'spanish': 'Español',
};

const languageToHubspot = (lang?: string): string =>
  lang ? (LANGUAGE_TO_HUBSPOT[lang.trim().toLowerCase()] || '') : '';

// Recap "2026 HC Lead Validity" → our determination.
const VALIDITY_TO_DETERMINATION: Record<string, LeadDetermination> = {
  'valid': 'valid', 'invalid': 'invalid', 'pending': 'pending',
};

// Recap "Lead- Funnel" → our status. Conservative: only the unambiguous ones
// drive status; everything is still stored raw on `hubspotFunnel`.
const FUNNEL_TO_STATUS: Record<string, LeadStatus> = {
  'enrolled': 'enrolled',
  'withdrawn': 'lost',
};

// =========================================================================
// Helpers
// =========================================================================

const splitName = (lead: Lead): { first: string; last: string } => {
  if (lead.parentFirstName || lead.parentLastName) {
    return { first: lead.parentFirstName || '', last: lead.parentLastName || '' };
  }
  const parts = (lead.parentName || '').trim().split(/\s+/);
  return { first: parts[0] || '', last: parts.slice(1).join(' ') || '' };
};

const childName = (c: any): string =>
  (c?.name || `${c?.firstName || ''} ${c?.lastName || ''}`.trim() || '').trim();

const parseRecapDate = (s?: string): Date | null => {
  if (!s) return null;
  const d = new Date(s.trim());
  return isNaN(d.getTime()) ? null : d;
};

// =========================================================================
// Clearwater / "Canvassing - HC" templates
// =========================================================================

const HC_CONSTANTS = {
  academicYear: '2026-2027',
  contactType: 'Prospective',
  offlineSource: 'Canvassing - HC',
  sourceActivity: 'Canvassing - HC',
  // Updated per client 2026-06-10: FY27 so re-engagements track correctly.
  poe: 'Canvassing - HC FY27',
};

export const HC_EXPORT_TEMPLATE: CsvExportTemplate = {
  id: 'hc-hubspot-export',
  name: 'Clearwater (HC) — HubSpot contact import',
  filenamePrefix: 'hc_hubspot_export',
  headers: [
    'Email', 'First name', 'Last name', 'Academic School Year', 'Phone number',
    'Contact Type', 'Language/Idioma', 'Offline Source Details', 'Source (Activity)',
    'Street address',
    'Student 1 Name', 'Student 1 Grade', 'Student 1 Campus',
    'Student 2 Name', 'Student 2 Grade', 'Student 2 Campus',
    'Student 3 Name', 'Student 3 Grade', 'Student 3 Campus',
    'Student 4 Name', 'Student 4 Grade', 'Student 4 Campus',
    'POE',
    'Voyage to Vista',
  ],
  buildRow: (lead: Lead, ctx: CsvExportContext): Record<string, string> => {
    const segFor = (id?: string) => ctx.segments.find(s => s.id === id);
    const { first, last } = splitName(lead);
    const kids = (lead.children || []).slice(0, 4);

    const row: Record<string, string> = {
      'Email': lead.email || '',
      'First name': first,
      'Last name': last,
      'Academic School Year': HC_CONSTANTS.academicYear,
      'Phone number': lead.phone || '',
      'Contact Type': HC_CONSTANTS.contactType,
      'Language/Idioma': languageToHubspot(lead.language),
      'Offline Source Details': HC_CONSTANTS.offlineSource,
      'Source (Activity)': HC_CONSTANTS.sourceActivity,
      'Street address': lead.address || '',
      'POE': HC_CONSTANTS.poe,
      // Parent RSVP to the client's enrollment event. Toggle on lead capture.
      'Voyage to Vista': lead.attendEvent ? 'RSVP' : '',
    };

    for (let i = 0; i < 4; i++) {
      const c: any = kids[i];
      row[`Student ${i + 1} Name`] = c ? childName(c) : '';
      row[`Student ${i + 1} Grade`] = c ? normalizeGradeForHubspot(c.grade) : '';
      // Per-student campus → segment (child override or lead's), resolved to the
      // HubSpot code (grade-banded where the campus splits by level).
      row[`Student ${i + 1} Campus`] = c ? resolveCampusCode(segFor(c.schoolSegmentId || lead.schoolSegmentId), c.grade) : '';
    }
    return row;
  },
};

export const HC_IMPORT_TEMPLATE: CsvImportTemplate = {
  id: 'hc-hubspot-recap',
  name: 'Clearwater (HC) — weekly HubSpot recap',
  source: 'HubSpot weekly recap',
  requiredHeaders: ['Record ID - Contact', 'First Name', 'Last Name', '2026 HC Lead Validity'],
  parseRow: (raw: Record<string, string>, rowNumber: number): ParsedImportRow | null => {
    const get = (k: string) => (raw[k] ?? '').toString().trim();
    const recordId = get('Record ID - Contact');
    const first = get('First Name');
    const last = get('Last Name');
    if (!recordId && !first && !last) return null; // blank row

    const updates: Partial<Lead> = {};
    const display: { field: string; value: string }[] = [];

    if (recordId) updates.hubspotContactId = recordId;

    const det = VALIDITY_TO_DETERMINATION[get('2026 HC Lead Validity').toLowerCase()];
    if (det) {
      updates.determination = det;
      display.push({ field: 'Determination', value: det });
    }

    const funnel = get('Lead- Funnel');
    if (funnel) {
      updates.hubspotFunnel = funnel;
      display.push({ field: 'Funnel', value: funnel });
      const st = FUNNEL_TO_STATUS[funnel.toLowerCase()];
      if (st) updates.status = st;
    }

    const contactType = get('Contact Type');
    if (contactType) {
      updates.hubspotContactType = contactType;
      display.push({ field: 'Contact Type', value: contactType });
      // Policy: enrolled-then-withdrawn within the project span is NOT an
      // enrollment (the lead stays valid/billed — see valid-stays-valid — but
      // must drop out of enrollment counts). The client signals withdrawal via
      // Contact Type, not always the funnel, so map it to status here too.
      if (contactType.toLowerCase() === 'withdrawn') updates.status = 'lost';
    }

    const lc = parseRecapDate(get('Last Contacted'));
    if (lc) {
      updates.lastContacted = Timestamp.fromDate(lc);
      display.push({ field: 'Last Contacted', value: get('Last Contacted') });
    }

    const matchKeys: LeadMatchKey[] = [];
    if (recordId) matchKeys.push({ kind: 'hubspotId', value: recordId });
    if (first || last) matchKeys.push({ kind: 'name', first, last });

    return { rowNumber, raw, matchKeys, updates, display };
  },
};

// Registry — add new client templates here.
export const CSV_EXPORT_TEMPLATES: CsvExportTemplate[] = [HC_EXPORT_TEMPLATE];
export const CSV_IMPORT_TEMPLATES: CsvImportTemplate[] = [HC_IMPORT_TEMPLATE];
